import { useRef, useMemo } from 'react'
import { useFrame } from '@react-three/fiber'
import * as THREE from 'three'
import { ORNAMENT_CONFIG } from '../../utils/constants'
import { generateChaosPosition, generateOrnamentPosition, getWeightedProgress } from '../../utils/positions'

interface OrnamentsProps {
  progress: number
}

interface OrnamentData {
  chaosPosition: THREE.Vector3
  targetPosition: THREE.Vector3
  color: THREE.Color
  scale: number
  weight: number
  rotationSpeed: number
}

// 金色发光彩球组件 - 真实金属质感（使用环境贴图反射）+ 晃动效果
function GoldenBauble({ position, color, scale, swingOffset, progress }: {
  position: THREE.Vector3
  color: THREE.Color
  scale: number
  swingOffset: number
  progress: number
}) {
  const groupRef = useRef<THREE.Group>(null)
  
  // 添加微微晃动效果
  useFrame((state) => {
    if (groupRef.current && progress > 0.5) {
      // 圣诞树状态下添加轻微晃动
      const time = state.clock.elapsedTime
      const swingAmount = 0.03 * progress // 晃动幅度随进度增加
      groupRef.current.rotation.x = Math.sin(time * 1.5 + swingOffset) * swingAmount
      groupRef.current.rotation.z = Math.cos(time * 1.2 + swingOffset * 0.7) * swingAmount
    }
  })
  
  return (
    <group ref={groupRef} position={position} scale={scale}>
      {/* 球体核心 - 真实金属质感 */}
      <mesh>
        <sphereGeometry args={[1, 64, 64]} />
        <meshStandardMaterial
          color={color}
          metalness={1.0}        // 完全金属
          roughness={0.08}       // 非常光滑，镜面反射
          envMapIntensity={2.5}  // 强环境反射
          emissive={color}
          emissiveIntensity={0.1} // 轻微自发光
        />
      </mesh>
      {/* 顶部装饰 - 金色金属 */}
      <mesh position={[0, 1.1, 0]}>
        <cylinderGeometry args={[0.12, 0.15, 0.15, 16]} />
        <meshStandardMaterial
          color="#FFD700"
          metalness={1.0}
          roughness={0.1}
          envMapIntensity={2}
          emissive="#FFD700"
          emissiveIntensity={0.1}
        />
      </mesh>
    </group>
  )
}

// 白色小灯泡组件 - 非常小的白色发光点
function WhiteBulb({ position, scale }: {
  position: THREE.Vector3
  scale: number
}) {
  return (
    <group position={position} scale={scale}>
      {/* 发光核心 - 纯白色 */}
      <mesh>
        <sphereGeometry args={[1, 8, 8]} />
        <meshBasicMaterial color="#FFFFFF" />
      </mesh>
      {/* 中层光晕 */}
      <mesh scale={1.8}>
        <sphereGeometry args={[1, 8, 8]} />
        <meshBasicMaterial color="#FFFFFF" transparent opacity={0.6} />
      </mesh>
      {/* 外层光晕 */}
      <mesh scale={2.5}>
        <sphereGeometry args={[1, 8, 8]} />
        <meshBasicMaterial color="#FFFACD" transparent opacity={0.25} />
      </mesh>
    </group>
  )
}

// 彩色灯光点组件 - 用于其他颜色的灯
function ColoredLight({ position, color, scale }: {
  position: THREE.Vector3
  color: THREE.Color
  scale: number
}) {
  return (
    <group position={position} scale={scale}>
      {/* 发光核心 */}
      <mesh>
        <sphereGeometry args={[1, 8, 8]} />
        <meshBasicMaterial color={color} />
      </mesh>
      {/* 中层光晕 */}
      <mesh scale={2}>
        <sphereGeometry args={[1, 8, 8]} />
        <meshBasicMaterial color={color} transparent opacity={0.5} />
      </mesh>
      {/* 外层光晕 */}
      <mesh scale={3}>
        <sphereGeometry args={[1, 8, 8]} />
        <meshBasicMaterial color={color} transparent opacity={0.2} />
      </mesh>
    </group>
  )
}

export function Ornaments({ progress }: OrnamentsProps) {
  const groupRef = useRef<THREE.Group>(null)
  
  // 生成装饰物数据 - 使用确定性随机数避免重合
  const { baubles, whiteBulbs, coloredLights } = useMemo(() => {
    const baubleData: OrnamentData[] = []
    const whiteBulbData: OrnamentData[] = []
    const coloredLightData: OrnamentData[] = []
    
    // 彩球颜色 - 参考图：80%金色, 20%红色
    const baubleColors = [
      '#FFD700', '#FFD700', '#FFD700', '#FFD700', // 金色
      '#FFC125', '#FFC125', '#FFC125', '#FFC125', // 深金色
      '#FFDF00', '#FFDF00', // 亮金色
      '#c62828', '#b71c1c', // 红色
    ]
    
    // 使用确定性随机数生成器避免重合
    const seededRandom = (seed: number) => {
      const x = Math.sin(seed * 12.9898 + 78.233) * 43758.5453
      return x - Math.floor(x)
    }
    
    // 生成金色彩球 - 使用更好的分布算法避免重合
    const baubleCount = 120 // 减少数量避免过于密集
    for (let i = 0; i < baubleCount; i++) {
      const colorIndex = Math.floor(seededRandom(i * 7) * baubleColors.length)
      
      // 使用螺旋分布 + 随机偏移，避免重合
      const layerIndex = Math.floor(i / 20) // 每层20个
      const indexInLayer = i % 20
      const baseAngle = (indexInLayer / 20) * Math.PI * 2 + layerIndex * 0.5
      const baseHeight = 1 + layerIndex * 1.5
      const baseRadius = 3.5 - layerIndex * 0.4
      
      // 添加随机偏移
      const angleOffset = (seededRandom(i * 3) - 0.5) * 0.5
      const heightOffset = (seededRandom(i * 5) - 0.5) * 0.8
      const radiusOffset = (seededRandom(i * 11) - 0.5) * 0.8
      
      const angle = baseAngle + angleOffset
      const height = baseHeight + heightOffset
      const radius = baseRadius + radiusOffset
      
      const targetPosition = new THREE.Vector3(
        Math.cos(angle) * radius,
        height,
        Math.sin(angle) * radius
      )
      
      baubleData.push({
        chaosPosition: generateChaosPosition(),
        targetPosition,
        color: new THREE.Color(baubleColors[colorIndex]),
        scale: 0.2 + seededRandom(i * 13) * 0.25, // 尺寸: 0.2-0.45，稍微减小避免重合
        weight: ORNAMENT_CONFIG.baubles.weight,
        rotationSpeed: (seededRandom(i * 17) - 0.5) * 3,
      })
    }
    
    // 生成白色小灯泡 - 大幅减少数量
    const whiteBulbCount = 12 // 少量灯泡
    for (let i = 0; i < whiteBulbCount; i++) {
      whiteBulbData.push({
        chaosPosition: generateChaosPosition(),
        targetPosition: generateOrnamentPosition('light'),
        color: new THREE.Color('#FFFFFF'),
        scale: 0.05 + Math.random() * 0.04, // 尺寸: 0.05-0.09
        weight: ORNAMENT_CONFIG.lights.weight,
        rotationSpeed: 0,
      })
    }
    
    // 生成极少量彩色灯光点
    const coloredLightCount = 5
    const lightColors = ['#FFD700', '#FF6B6B', '#FFFFFF']
    for (let i = 0; i < coloredLightCount; i++) {
      const colorIndex = Math.floor(Math.random() * lightColors.length)
      coloredLightData.push({
        chaosPosition: generateChaosPosition(),
        targetPosition: generateOrnamentPosition('light'),
        color: new THREE.Color(lightColors[colorIndex]),
        scale: 0.08 + Math.random() * 0.06,
        weight: ORNAMENT_CONFIG.lights.weight,
        rotationSpeed: 0,
      })
    }
    
    return { baubles: baubleData, whiteBulbs: whiteBulbData, coloredLights: coloredLightData }
  }, [])
  
  // 计算当前位置
  const getCurrentPosition = (data: OrnamentData) => {
    const weightedProgress = getWeightedProgress(progress, data.weight)
    return new THREE.Vector3().lerpVectors(
      data.chaosPosition,
      data.targetPosition,
      weightedProgress
    )
  }
  
  // 动画更新
  const timeRef = useRef(0)
  useFrame((_, delta) => {
    timeRef.current += delta
  })
  
  return (
    <group ref={groupRef}>
      {/* 金色彩球 - 主要装饰，带晃动效果 */}
      {baubles.map((bauble, index) => (
        <GoldenBauble
          key={`bauble-${index}`}
          position={getCurrentPosition(bauble)}
          color={bauble.color}
          scale={bauble.scale}
          swingOffset={index * 0.5} // 每个球不同的晃动相位
          progress={progress}
        />
      ))}
      
      {/* 白色小灯泡 - 点缀在金色球之间 */}
      {whiteBulbs.map((bulb, index) => (
        <WhiteBulb
          key={`white-bulb-${index}`}
          position={getCurrentPosition(bulb)}
          scale={bulb.scale * (0.5 + progress * 0.5)}
        />
      ))}
      
      {/* 彩色灯光点 */}
      {coloredLights.map((light, index) => (
        <ColoredLight
          key={`colored-light-${index}`}
          position={getCurrentPosition(light)}
          color={light.color}
          scale={light.scale * progress}
        />
      ))}
    </group>
  )
}